// Pushing the Usages sheet back to the project.
//
// The sheet's contents and the diff live in data/usagesTable.ts, free of the
// SDK; this is only the sending.

import { deleteUsage, removeUsageFields, writeUsages } from './adapter';
import type { UsageDiff } from '@/data/usagesTable';

export * from '@/data/usagesTable';

export interface PushResult {
  ok: boolean;
  updated: number;
  deleted: number;
  detail: string;
}

/**
 * Send the diff.
 *
 * `updateUsage(name, { join })` merges WITHIN `join`, so a single-field write
 * preserves every other rate on that usage — the opposite of `updateBlock`,
 * which replaces wholesale.
 *
 * A deletion removes the usage from the PROJECT, not just from this sheet, so
 * it is confirmed by name and never inferred from a stray blank. A RENAME is
 * not one of those: it drops the old name because the user moved it, which is
 * the act they already performed, not a second one to agree to.
 */
export async function pushUsages(
  diff: UsageDiff,
  confirmDelete: (names: string[]) => boolean
): Promise<PushResult> {
  /*
   * Edits and additions are the SAME write.
   *
   * Usages live in the core Giraffe app at
   * `projectAppsByAppID["1"].featureCategories.usage`, and writing one that is
   * not there yet creates it — so there is no separate create path, and no way
   * for the two to disagree.
   */
  const patches: Record<string, { join?: Record<string, unknown>; default?: Record<string, unknown> }> = {};

  for (const change of diff.changes) {
    const patch = (patches[change.name] ??= { join: {} });
    (patch.join ??= {})[change.field] = change.to;
  }

  /*
   * A rename is a create plus a delete — there is no rename RPC, usages being
   * keyed by their name.
   *
   * The new usage starts from everything the project holds under the OLD name,
   * so the move keeps what the sheet cannot push on its own: the nested values
   * it can only show as JSON. The row's own cells land on top, below, because
   * the new name is in `added` too.
   */
  for (const rename of diff.renamed) {
    const carried: Record<string, unknown> = { ...rename.fields };
    // Injected when the usage was flattened for the sheet; the key IS the name.
    delete carried.name;
    delete carried.usage;
    patches[rename.to] = { join: carried, default: {} };
  }

  const untyped: string[] = [];
  for (const addition of diff.added) {
    const join: Record<string, unknown> = { ...patches[addition.name]?.join, ...addition.fields };
    // A usage with no type does not render on the map. Defaulted rather than
    // refused, and named in the result so it is not a silent choice. A renamed
    // usage already carries the old one's type, so it is never one of these.
    if (join.type === undefined || join.type === '') {
      join.type = 'buildingSection';
      untyped.push(addition.name);
    }
    if (join.color === undefined || join.color === '') join.color = '#989CA0';
    patches[addition.name] = { join, default: {} };
  }

  let written = 0;
  if (Object.keys(patches).length > 0) {
    const res = await writeUsages(patches);
    if (!res.ok) {
      return { ok: false, updated: 0, deleted: 0, detail: res.reason ?? 'The write was refused.' };
    }
    written = Object.keys(patches).length;
  }

  // The old name of a rename goes unconditionally; a cleared row only if the
  // user said so, and only after the writes above have landed.
  const drop = diff.renamed.map((r) => r.from);
  const confirmed = diff.removed.length > 0 && confirmDelete(diff.removed);
  if (confirmed) drop.push(...diff.removed);

  let dropped = 0;
  for (const name of drop) {
    const res = await deleteUsage(name);
    if (!res.ok) {
      return {
        ok: false,
        updated: written,
        deleted: dropped,
        detail: `Wrote ${written}, then could not delete "${name}": ${res.reason ?? 'refused'}.`,
      };
    }
    dropped += 1;
  }
  const deleted = confirmed ? dropped - diff.renamed.length : 0;

  const parts: string[] = [];
  const created = diff.added.length - diff.renamed.length;
  const edited = written - diff.added.length;
  if (created > 0) parts.push(`created ${created}`);
  if (edited > 0) parts.push(`updated ${edited}`);
  for (const rename of diff.renamed) parts.push(`renamed ${rename.from} to ${rename.to}`);
  if (deleted > 0) parts.push(`deleted ${deleted}`);
  const note = untyped.length > 0 ? ` ${untyped.join(', ')} defaulted to a buildingSection — set Type to change it.` : '';
  return {
    ok: true,
    updated: written,
    deleted,
    detail: parts.length > 0 ? `${parts.join(', ')}.${note}` : 'Nothing to push.',
  };
}

/** Drop a field from every usage. Confirmed by the caller — it is not undoable. */
export async function pushFieldRemoval(fields: readonly string[]): Promise<PushResult> {
  const res = await removeUsageFields(fields);
  if (!res.ok) {
    return { ok: false, updated: 0, deleted: 0, detail: res.reason ?? 'The write was refused.' };
  }
  const list = fields.join(', ');
  return { ok: true, updated: 0, deleted: fields.length, detail: `Removed ${list} from every usage.` };
}
